import { Component, OnDestroy, OnInit, TemplateRef } from '@angular/core';
import { Router } from '@angular/router';
import { forkJoin, Subscription } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { Store } from '@ngxs/store';

import { BusinessDeveloperTurnoverOverview, DivisionTurnoverOverview, PersonCostDetail, ProjectMarginDetail, TurnoverDetail, YukiReconciliationResult, YukiAccountingVerification, DivisionAccountingVerification, YukiTransactionItem } from '../../models/Financial/DivisionTurnoverOverview';
import { DateNavigationOptions, DateNavigationType, SelectedDateClass } from '../../shared/date-navigation/date-navigation.component';
import { FinancialService } from '../financial.service';
import { DivisionTargetModalComponent } from '../division-target-modal/division-target-modal.component';
import { DivisionDetailModalComponent } from '../division-detail-modal/division-detail-modal.component';
import { BusinessDeveloperTargetModalComponent } from '../business-developer-target-modal/business-developer-target-modal.component';
import { UserState } from '../../users/user.state';

@Component({
    selector: 'app-division-graphs',
    templateUrl: './division-graphs.component.html',
    styleUrls: ['./division-graphs.component.scss'],
})
export class DivisionGraphsComponent implements OnInit, OnDestroy {
    private static readonly ALL_BUSINESS_DEVELOPERS = 'ALL';

    loadingData = false;
    overviewData: DivisionTurnoverOverview[] = [];
    businessDeveloperOverviewData: BusinessDeveloperTurnoverOverview[] = [];
    selectedBusinessDeveloperUuid: string = DivisionGraphsComponent.ALL_BUSINESS_DEVELOPERS;
    dataSource: 'RESUMO' | 'YUKI' = 'RESUMO';
    private subscriptions = new Subscription();

    readonly monthLabels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    readonly fullMonthLabels = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    readonly divisionOrder = ['CONSULTANCY', 'CUSTOM', 'WEBSITE', 'HOSTING', 'INTERN', 'OTHER'];
    readonly divisionLabels: { [key: string]: string } = {
        CONSULTANCY: 'Consultancy',
        CUSTOM: 'Custom (SOM)',
        WEBSITE: 'Website',
        HOSTING: 'Hosting',
        INTERN: 'Intern',
        OTHER: 'Other',
    };

    dateNavigationOptions: DateNavigationOptions = {
        selectedDate: new Date(),
        dateNavigationType: DateNavigationType.YEAR,
        selectedDateClass: SelectedDateClass.SMALL,
    };

    constructor(
        private financialService: FinancialService,
        private modalService: NgbModal,
        private toastr: ToastrService,
        private store: Store,
        private router: Router,
    ) {}

    ngOnInit(): void {
        this.subscriptions.add(
            this.financialService.getTargetsDataSource().subscribe({
                next: (result) => {
                    this.dataSource = result.dataSource as 'RESUMO' | 'YUKI';
                    this.fetchData();
                },
                error: () => this.fetchData(),
            })
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    updateSelectedDate(): void {
        this.fetchData();
    }

    get currentYear(): number {
        return this.dateNavigationOptions.selectedDate.getFullYear();
    }

    get currentMonth(): number {
        const now = new Date();
        return now.getFullYear() === this.currentYear ? now.getMonth() : -1;
    }

    // ── Data Accessors ─────────────────────────────────────────────────────────

    getTurnover(division: string, monthIndex: number): number {
        if (this.dataSource === 'YUKI') {
            const v = this.yukiVerificationMap[division];
            return v ? (v.yukiMonthly[monthIndex] || 0) : 0;
        }
        const row = this.overviewData.find(d => d.division === division);
        return row ? (row.monthlyTurnover[monthIndex] || 0) : 0;
    }

    getDivisionCost(division: string, monthIndex: number): number {
        const row = this.overviewData.find(d => d.division === division);
        return row ? (row.monthlyCosts?.[monthIndex] || 0) : 0;
    }

    getDivisionMargin(division: string, monthIndex: number): number {
        const row = this.overviewData.find(d => d.division === division);
        if (!row) return 0;
        if (this.dataSource === 'YUKI') {
            // Do not expose profit per division in Yuki mode: revenue is mapped per division,
            // but costs are not. Only the monthly total profit/loss may be shown.
            return 0;
        }
        if (row.monthlyMargin && row.monthlyMargin.length > monthIndex) {
            return row.monthlyMargin[monthIndex] || 0;
        }
        return this.getTurnover(division, monthIndex) - this.getDivisionCost(division, monthIndex);
    }

    getTarget(division: string, monthIndex: number): number {
        const row = this.overviewData.find(d => d.division === division);
        return row ? (row.monthlyTargets[monthIndex] || 0) : 0;
    }

    getDetails(division: string, monthIndex: number): TurnoverDetail[] {
        const row = this.overviewData.find(d => d.division === division);
        return row && row.monthlyDetails ? (row.monthlyDetails[monthIndex] || []) : [];
    }

    getDivisionYearTotal(division: string): number {
        if (this.dataSource === 'YUKI') {
            const v = this.yukiVerificationMap[division];
            return v ? v.yukiYearTotal : 0;
        }
        const row = this.overviewData.find(d => d.division === division);
        return row ? row.yearTotal : 0;
    }

    getDivisionYearMarginTotal(division: string): number {
        const row = this.overviewData.find(d => d.division === division);
        if (!row) return 0;
        if (this.dataSource === 'YUKI') {
            return this.monthLabels.reduce((sum, _, monthIndex) => sum + this.getDivisionMargin(division, monthIndex), 0);
        }
        return row.yearMarginTotal !== undefined && row.yearMarginTotal !== null
            ? row.yearMarginTotal
            : this.monthLabels.reduce((sum, _, monthIndex) => sum + this.getDivisionMargin(division, monthIndex), 0);
    }

    getDivisionYearTarget(division: string): number {
        const row = this.overviewData.find(d => d.division === division);
        return row ? row.yearTargetTotal : 0;
    }

    getMonthTotal(monthIndex: number): number {
        if (this.dataSource === 'YUKI') {
            return Object.values(this.yukiVerificationMap)
                .reduce((sum, v) => sum + (v.yukiMonthly[monthIndex] || 0), 0);
        }
        return this.overviewData.reduce((sum, d) => sum + (d.monthlyTurnover[monthIndex] || 0), 0);
    }

    getMonthMarginTotal(monthIndex: number): number {
        if (this.dataSource === 'YUKI') {
            return this.yukiVerification?.accountingMonthlyResult?.[monthIndex] || 0;
        }
        return this.overviewData.reduce((sum, d) => sum + this.getDivisionMargin(d.division, monthIndex), 0);
    }

    shouldShowMonthMargin(monthIndex: number): boolean {
        if (this.currentYear < new Date().getFullYear()) return true;
        return monthIndex < new Date().getMonth();
    }

    hasMonthMarginTotal(monthIndex: number): boolean {
        if (!this.shouldShowMonthMargin(monthIndex)) return false;
        if (this.dataSource !== 'YUKI') return true;
        return !!this.yukiVerification?.accountingMonthlyResult
            && this.yukiVerification.accountingMonthlyResult.length > monthIndex;
    }

    getMonthTargetTotal(monthIndex: number): number {
        return this.overviewData.reduce((sum, d) => sum + (d.monthlyTargets[monthIndex] || 0), 0);
    }

    getGrandTotal(): number {
        if (this.dataSource === 'YUKI') {
            return Object.values(this.yukiVerificationMap)
                .reduce((sum, v) => sum + v.yukiYearTotal, 0);
        }
        return this.overviewData.reduce((sum, d) => sum + d.yearTotal, 0);
    }

    getGrandMarginTotal(): number {
        return this.overviewData.reduce((sum, d) => sum + this.getDivisionYearMarginTotal(d.division), 0);
    }

    getGrandTargetTotal(): number {
        return this.overviewData.reduce((sum, d) => sum + d.yearTargetTotal, 0);
    }

    get selectedBusinessDeveloper(): BusinessDeveloperTurnoverOverview | null {
        if (this.isAllBusinessDevelopersSelected()) return null;
        return this.businessDeveloperOverviewData.find(d => d.businessDeveloperUuid === this.selectedBusinessDeveloperUuid) || null;
    }

    isAllBusinessDevelopersSelected(): boolean {
        return !this.selectedBusinessDeveloperUuid
            || this.selectedBusinessDeveloperUuid === DivisionGraphsComponent.ALL_BUSINESS_DEVELOPERS;
    }

    getSelectedBusinessDeveloperTargetPercentage(): number {
        const selected = this.selectedBusinessDeveloper;
        if (!selected || selected.yearTargetTotal <= 0) return 0;
        return this.getTargetPercentage(selected.yearTotal, selected.yearTargetTotal);
    }

    getSelectedBusinessDeveloperAchievement(): number {
        const selected = this.selectedBusinessDeveloper;
        if (!selected) return 0;
        return selected.netResult !== undefined && selected.netResult !== null
            ? selected.netResult
            : this.getSelectedBusinessDeveloperRealizedMargin() - selected.yearCostTotal;
    }

    getSelectedBusinessDeveloperRealizedMargin(): number {
        const selected = this.selectedBusinessDeveloper;
        if (!selected) return 0;
        if (selected.yearRealizedMargin !== undefined && selected.yearRealizedMargin !== null) return selected.yearRealizedMargin;
        return selected.yearTotal;
    }

    getSelectedBusinessDeveloperMarginTooltip(): string {
        const selected = this.selectedBusinessDeveloper;
        if (!selected?.projectMarginDetails?.length) return 'No project margin details available.';
        return selected.projectMarginDetails
            .slice(0, 20)
            .map((detail) =>
                `${detail.projectName}: revenue ${this.formatCurrency(detail.revenue)} - costs ${this.formatCurrency(detail.costs)} = margin ${this.formatCurrency(detail.margin)} (${detail.costSourceLabel})`,
            )
            .join('\n');
    }

    getSelectedBusinessDeveloperMarginDetails(): ProjectMarginDetail[] {
        const selected = this.selectedBusinessDeveloper;
        if (!selected?.projectMarginDetails?.length) return [];
        return [...selected.projectMarginDetails].sort((a, b) => b.margin - a.margin);
    }

    getSelectedBusinessDeveloperProjectRevenueTotal(): number {
        const details = this.getSelectedBusinessDeveloperMarginDetails();
        if (details.length === 0) return this.selectedBusinessDeveloper?.yearTotal || 0;
        return details.reduce((sum, detail) => sum + (detail.revenue || 0), 0);
    }

    getSelectedBusinessDeveloperProjectCostTotal(): number {
        return this.getSelectedBusinessDeveloperMarginDetails()
            .reduce((sum, detail) => sum + (detail.costs || 0), 0);
    }

    getSelectedBusinessDeveloperCostTooltip(): string {
        const selected = this.selectedBusinessDeveloper;
        if (!selected?.personCostDetails?.length) return `${this.getSelectedBusinessDeveloperCostSourceLabel()}: ${this.formatCurrency(selected?.yearCostTotal || 0)}`;
        return selected.personCostDetails
            .slice(0, 20)
            .map((detail) =>
                `${detail.monthLabel}: linked invoices ${this.formatCurrency(detail.linkedInvoiceCost)} + payroll ${this.formatCurrency(detail.payrollCost)} = ${this.formatCurrency(detail.totalCost)} (${detail.costSourceLabel})`,
            )
            .join('\n');
    }

    getSelectedBusinessDeveloperCostDetails(): PersonCostDetail[] {
        const selected = this.selectedBusinessDeveloper;
        return selected?.personCostDetails || [];
    }

    getSelectedBusinessDeveloperPortfolioPeriodLabel(): string {
        return this.selectedBusinessDeveloper?.portfolioPeriodLabel || '-';
    }

    getSelectedBusinessDeveloperPersonCostPeriodLabel(): string {
        return this.selectedBusinessDeveloper?.personCostPeriodLabel || '-';
    }

    getSelectedBusinessDeveloperLinkedInvoiceCostTotal(): number {
        return this.getSelectedBusinessDeveloperCostDetails()
            .reduce((sum, detail) => sum + (detail.linkedInvoiceCost || 0), 0);
    }

    getSelectedBusinessDeveloperPayrollCostTotal(): number {
        return this.getSelectedBusinessDeveloperCostDetails()
            .reduce((sum, detail) => sum + (detail.payrollCost || 0), 0);
    }

    openSelectedBusinessDeveloperMarginOverview(content: TemplateRef<any>): void {
        if (!this.selectedBusinessDeveloper) return;
        this.modalService.open(content, {
            windowClass: 'modal-prompt modal-prompt--wide',
            animation: false,
        });
    }

    openSelectedBusinessDeveloperCostOverview(content: TemplateRef<any>): void {
        if (!this.selectedBusinessDeveloper) return;
        this.modalService.open(content, {
            windowClass: 'modal-prompt modal-prompt--wide',
            animation: false,
        });
    }

    getSelectedBusinessDeveloperRoi(): number | null {
        const selected = this.selectedBusinessDeveloper;
        if (!selected) return null;
        if (selected.roiPercentage !== undefined && selected.roiPercentage !== null) return selected.roiPercentage;
        if (!selected.yearCostTotal) return null;
        return Math.round(((this.getSelectedBusinessDeveloperRealizedMargin() - selected.yearCostTotal) / selected.yearCostTotal) * 1000) / 10;
    }

    getSelectedBusinessDeveloperRoiLabel(): string {
        const roi = this.getSelectedBusinessDeveloperRoi();
        if (roi === null) return '-';
        const sign = roi > 0 ? '+' : '';
        return `${sign}${roi.toLocaleString('nl-BE', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
    }

    getSelectedBusinessDeveloperProfitabilityStatus(): string {
        const selected = this.selectedBusinessDeveloper;
        if (!selected) return '';
        if (selected.profitabilityStatus) return selected.profitabilityStatus;
        return this.getSelectedBusinessDeveloperAchievement() >= 0 ? 'Profitable' : 'Not profitable';
    }

    getSelectedBusinessDeveloperBreakEvenLabel(): string {
        const selected = this.selectedBusinessDeveloper;
        if (!selected) return 'Not reached';
        return selected.breakEvenLabel || this.getSelectedBusinessDeveloperPaybackLabel();
    }

    getSelectedBusinessDeveloperCostSourceLabel(): string {
        const selected = this.selectedBusinessDeveloper;
        return selected?.costSourceLabel || 'Cost data';
    }

    getSelectedBusinessDeveloperTypeLabel(): string {
        const selected = this.selectedBusinessDeveloper;
        return selected?.employeeType || 'Employee';
    }

    getSelectedBusinessDeveloperPaybackLabel(): string {
        const selected = this.selectedBusinessDeveloper;
        if (!selected || !selected.paybackMonth) return 'Not reached';
        return this.fullMonthLabels[selected.paybackMonth - 1] || `Month ${selected.paybackMonth}`;
    }

    getSelectedBusinessDeveloperMonthTurnover(monthIndex: number): number {
        const selected = this.selectedBusinessDeveloper;
        return selected ? (selected.monthlyTurnover[monthIndex] || 0) : this.getMonthTotal(monthIndex);
    }

    getSelectedBusinessDeveloperMonthTarget(monthIndex: number): number {
        const selected = this.selectedBusinessDeveloper;
        return selected ? (selected.monthlyTargets[monthIndex] || 0) : this.getMonthTargetTotal(monthIndex);
    }

    getSelectedBusinessDeveloperMonthCost(monthIndex: number): number {
        const selected = this.selectedBusinessDeveloper;
        return selected ? (selected.monthlyCosts[monthIndex] || 0) : 0;
    }

    getSelectedBusinessDeveloperMonthRealizedMargin(monthIndex: number): number {
        const selected = this.selectedBusinessDeveloper;
        if (!selected) return 0;
        if (selected.monthlyRealizedMargin && selected.monthlyRealizedMargin.length > monthIndex) {
            return selected.monthlyRealizedMargin[monthIndex] || 0;
        }
        return this.getSelectedBusinessDeveloperMonthTurnover(monthIndex);
    }

    getSelectedBusinessDeveloperMonthAchievement(monthIndex: number): number {
        return this.getSelectedBusinessDeveloperMonthRealizedMargin(monthIndex) - this.getSelectedBusinessDeveloperMonthCost(monthIndex);
    }

    getSelectedBusinessDeveloperYearClass(): string {
        const selected = this.selectedBusinessDeveloper;
        if (!selected || selected.yearTargetTotal <= 0) return '';

        const now = new Date();
        if (selected.yearTotal >= selected.yearTargetTotal) return 'target-met';
        if (this.currentYear < now.getFullYear()) return 'target-missed';
        return 'target-future';
    }

    getBusinessDeveloperMonthClass(monthIndex: number): string {
        const target = this.getSelectedBusinessDeveloperMonthTarget(monthIndex);
        if (target <= 0) return '';

        const turnover = this.getSelectedBusinessDeveloperMonthTurnover(monthIndex);
        const now = new Date();
        const isCurrentYear = this.currentYear === now.getFullYear();

        if (turnover >= target) return 'target-met';
        if (isCurrentYear && monthIndex < now.getMonth()) return 'target-missed';
        if (!isCurrentYear && this.currentYear < now.getFullYear()) return 'target-missed';
        return 'target-future';
    }

    // ── Cell Status Logic ──────────────────────────────────────────────────────

    getCellClass(division: string, monthIndex: number): string {
        const target = this.getTarget(division, monthIndex);
        if (target <= 0) return '';

        const turnover = this.getTurnover(division, monthIndex);
        const now = new Date();
        const isCurrentYear = this.currentYear === now.getFullYear();

        if (turnover >= target) return 'target-met';
        if (isCurrentYear && monthIndex < now.getMonth()) return 'target-missed';
        if (!isCurrentYear && this.currentYear < now.getFullYear()) return 'target-missed';
        return 'target-future';
    }

    getMonthTotalClass(monthIndex: number): string {
        const target = this.getMonthTargetTotal(monthIndex);
        if (target <= 0) return '';

        const turnover = this.getMonthTotal(monthIndex);
        const now = new Date();
        const isCurrentYear = this.currentYear === now.getFullYear();

        if (turnover >= target) return 'target-met';
        if (isCurrentYear && monthIndex < now.getMonth()) return 'target-missed';
        if (!isCurrentYear && this.currentYear < now.getFullYear()) return 'target-missed';
        return 'target-future';
    }

    getYearTotalClass(division: string): string {
        const target = this.getDivisionYearTarget(division);
        if (target <= 0) return '';

        const turnover = this.getDivisionYearTotal(division);
        const now = new Date();

        if (turnover >= target) return 'target-met';
        if (this.currentYear < now.getFullYear()) return 'target-missed';
        return 'target-future';
    }

    // ── Detail Modal ─────────────────────────────────────────────────────────

    openDetailModal(division: string, monthIndex: number): void {
        const details = this.getDetails(division, monthIndex);
        const amount = this.getTurnover(division, monthIndex);
        const target = this.getTarget(division, monthIndex);

        const modalRef = this.modalService.open(DivisionDetailModalComponent, {
            windowClass: 'modal-huge',
            animation: false,
        });

        modalRef.componentInstance.divisionLabel = this.divisionLabels[division] || division;
        modalRef.componentInstance.monthLabel = this.fullMonthLabels[monthIndex];
        modalRef.componentInstance.year = this.currentYear;
        modalRef.componentInstance.amount = amount;
        modalRef.componentInstance.target = target;
        modalRef.componentInstance.details = details.sort((a, b) => b.amount - a.amount);

        // Pass Yuki transactions for this division+month
        const v = this.yukiVerificationMap[division];
        if (v && v.yukiTransactionsByMonth && v.yukiTransactionsByMonth[monthIndex]) {
            modalRef.componentInstance.yukiTransactions = v.yukiTransactionsByMonth[monthIndex];
            modalRef.componentInstance.yukiTotal = v.yukiMonthly[monthIndex] || 0;
        }

        // Pass adjacent months for AI context
        if (monthIndex > 0) {
            modalRef.componentInstance.prevMonthLabel = this.fullMonthLabels[monthIndex - 1];
            modalRef.componentInstance.prevMonthResumoInvoices = this.getDetails(division, monthIndex - 1);
            if (v && v.yukiTransactionsByMonth && v.yukiTransactionsByMonth[monthIndex - 1]) {
                modalRef.componentInstance.prevMonthYukiTransactions = v.yukiTransactionsByMonth[monthIndex - 1];
            }
        }
        if (monthIndex < 11) {
            modalRef.componentInstance.nextMonthLabel = this.fullMonthLabels[monthIndex + 1];
            modalRef.componentInstance.nextMonthResumoInvoices = this.getDetails(division, monthIndex + 1);
            if (v && v.yukiTransactionsByMonth && v.yukiTransactionsByMonth[monthIndex + 1]) {
                modalRef.componentInstance.nextMonthYukiTransactions = v.yukiTransactionsByMonth[monthIndex + 1];
            }
        }

        // Pass other divisions' Yuki transactions for cross-account checking
        const otherDivYuki: { division: string, transactions: YukiTransactionItem[] }[] = [];
        for (const [div, ver] of Object.entries(this.yukiVerificationMap)) {
            if (div !== division && ver.yukiTransactionsByMonth && ver.yukiTransactionsByMonth[monthIndex]) {
                otherDivYuki.push({
                    division: this.divisionLabels[div] || div,
                    transactions: ver.yukiTransactionsByMonth[monthIndex],
                });
            }
        }
        modalRef.componentInstance.otherDivisionsYuki = otherDivYuki;
    }

    openYearDetailModal(division: string): void {
        const row = this.overviewData.find(d => d.division === division);
        if (!row) return;

        const allDetails: TurnoverDetail[] = [];
        if (row.monthlyDetails) {
            for (const monthDetails of row.monthlyDetails) {
                if (monthDetails) allDetails.push(...monthDetails);
            }
        }

        const modalRef = this.modalService.open(DivisionDetailModalComponent, {
            windowClass: 'modal-huge',
            animation: false,
        });

        modalRef.componentInstance.divisionLabel = this.divisionLabels[division] || division;
        modalRef.componentInstance.monthLabel = 'Year Total';
        modalRef.componentInstance.year = this.currentYear;
        modalRef.componentInstance.amount = row.yearTotal;
        modalRef.componentInstance.target = row.yearTargetTotal;
        modalRef.componentInstance.details = allDetails.sort((a, b) => b.amount - a.amount);

        // Pass all Yuki transactions for the year
        const v = this.yukiVerificationMap[division];
        if (v && v.yukiTransactionsByMonth) {
            const allYukiTx = [];
            for (const monthTx of v.yukiTransactionsByMonth) {
                if (monthTx) allYukiTx.push(...monthTx);
            }
            modalRef.componentInstance.yukiTransactions = allYukiTx;
            modalRef.componentInstance.yukiTotal = v.yukiYearTotal || 0;
        }
    }

    // ── Total Row Detail Modals ──────────────────────────────────────────────

    openTotalMonthDetailModal(monthIndex: number): void {
        // Aggregate all divisions for this month
        const allDetails: TurnoverDetail[] = [];
        const allYukiTx: any[] = [];
        let yukiTotal = 0;

        for (const row of this.overviewData) {
            const monthDetails = row.monthlyDetails ? row.monthlyDetails[monthIndex] : null;
            if (monthDetails) allDetails.push(...monthDetails);

            const v = this.yukiVerificationMap[row.division];
            if (v && v.yukiTransactionsByMonth && v.yukiTransactionsByMonth[monthIndex]) {
                allYukiTx.push(...v.yukiTransactionsByMonth[monthIndex]);
            }
            if (v && v.yukiMonthly) {
                yukiTotal += v.yukiMonthly[monthIndex] || 0;
            }
        }

        const modalRef = this.modalService.open(DivisionDetailModalComponent, {
            windowClass: 'modal-huge',
            animation: false,
        });

        modalRef.componentInstance.divisionLabel = 'Total';
        modalRef.componentInstance.monthLabel = this.fullMonthLabels[monthIndex];
        modalRef.componentInstance.year = this.currentYear;
        modalRef.componentInstance.amount = this.getMonthTotal(monthIndex);
        modalRef.componentInstance.target = this.getMonthTargetTotal(monthIndex);
        modalRef.componentInstance.details = allDetails.sort((a, b) => b.amount - a.amount);
        modalRef.componentInstance.yukiTransactions = allYukiTx;
        modalRef.componentInstance.yukiTotal = yukiTotal;

        // Adjacent months for AI context
        if (monthIndex > 0) {
            modalRef.componentInstance.prevMonthLabel = this.fullMonthLabels[monthIndex - 1];
            const prevDetails: TurnoverDetail[] = [];
            const prevYukiTx: any[] = [];
            for (const row of this.overviewData) {
                const md = row.monthlyDetails ? row.monthlyDetails[monthIndex - 1] : null;
                if (md) prevDetails.push(...md);
                const v = this.yukiVerificationMap[row.division];
                if (v?.yukiTransactionsByMonth?.[monthIndex - 1]) prevYukiTx.push(...v.yukiTransactionsByMonth[monthIndex - 1]);
            }
            modalRef.componentInstance.prevMonthResumoInvoices = prevDetails;
            modalRef.componentInstance.prevMonthYukiTransactions = prevYukiTx;
        }
        if (monthIndex < 11) {
            modalRef.componentInstance.nextMonthLabel = this.fullMonthLabels[monthIndex + 1];
            const nextDetails: TurnoverDetail[] = [];
            const nextYukiTx: any[] = [];
            for (const row of this.overviewData) {
                const md = row.monthlyDetails ? row.monthlyDetails[monthIndex + 1] : null;
                if (md) nextDetails.push(...md);
                const v = this.yukiVerificationMap[row.division];
                if (v?.yukiTransactionsByMonth?.[monthIndex + 1]) nextYukiTx.push(...v.yukiTransactionsByMonth[monthIndex + 1]);
            }
            modalRef.componentInstance.nextMonthResumoInvoices = nextDetails;
            modalRef.componentInstance.nextMonthYukiTransactions = nextYukiTx;
        }
    }

    openTotalYearDetailModal(): void {
        const allDetails: TurnoverDetail[] = [];
        const allYukiTx: any[] = [];
        let yukiTotal = 0;

        for (const row of this.overviewData) {
            if (row.monthlyDetails) {
                for (const monthDetails of row.monthlyDetails) {
                    if (monthDetails) allDetails.push(...monthDetails);
                }
            }
            const v = this.yukiVerificationMap[row.division];
            if (v && v.yukiTransactionsByMonth) {
                for (const monthTx of v.yukiTransactionsByMonth) {
                    if (monthTx) allYukiTx.push(...monthTx);
                }
            }
            if (v) yukiTotal += v.yukiYearTotal || 0;
        }

        const modalRef = this.modalService.open(DivisionDetailModalComponent, {
            windowClass: 'modal-huge',
            animation: false,
        });

        modalRef.componentInstance.divisionLabel = 'Total';
        modalRef.componentInstance.monthLabel = 'Year Total';
        modalRef.componentInstance.year = this.currentYear;
        modalRef.componentInstance.amount = this.getGrandTotal();
        modalRef.componentInstance.target = this.getGrandTargetTotal();
        modalRef.componentInstance.details = allDetails.sort((a, b) => b.amount - a.amount);
        modalRef.componentInstance.yukiTransactions = allYukiTx;
        modalRef.componentInstance.yukiTotal = yukiTotal;
    }

    openBusinessDeveloperMonthDetailModal(monthIndex: number): void {
        const selected = this.selectedBusinessDeveloper;
        if (!selected) return;

        const modalRef = this.modalService.open(DivisionDetailModalComponent, {
            windowClass: 'modal-huge',
            animation: false,
        });

        modalRef.componentInstance.divisionLabel = selected.businessDeveloperName;
        modalRef.componentInstance.monthLabel = this.fullMonthLabels[monthIndex];
        modalRef.componentInstance.year = this.currentYear;
        modalRef.componentInstance.amount = this.getSelectedBusinessDeveloperMonthTurnover(monthIndex);
        modalRef.componentInstance.target = this.getSelectedBusinessDeveloperMonthTarget(monthIndex);
        modalRef.componentInstance.details = (selected.monthlyDetails?.[monthIndex] || []).sort((a, b) => b.amount - a.amount);
    }

    openBusinessDeveloperYearDetailModal(): void {
        const selected = this.selectedBusinessDeveloper;
        if (!selected) return;

        const allDetails: TurnoverDetail[] = [];
        if (selected.monthlyDetails) {
            for (const monthDetails of selected.monthlyDetails) {
                if (monthDetails) allDetails.push(...monthDetails);
            }
        }

        const modalRef = this.modalService.open(DivisionDetailModalComponent, {
            windowClass: 'modal-huge',
            animation: false,
        });

        modalRef.componentInstance.divisionLabel = selected.businessDeveloperName;
        modalRef.componentInstance.monthLabel = 'Year Total';
        modalRef.componentInstance.year = this.currentYear;
        modalRef.componentInstance.amount = selected.yearTotal;
        modalRef.componentInstance.target = selected.yearTargetTotal;
        modalRef.componentInstance.details = allDetails.sort((a, b) => b.amount - a.amount);
    }

    // ── Target Edit Modal ──────────────────────────────────────────────────────

    openTargetModal(): void {
        const modalRef = this.modalService.open(DivisionTargetModalComponent, {
            windowClass: 'modal-prompt modal-prompt--wide',
            animation: false,
        });

        const activeDivisions = this.overviewData.map(d => d.division);
        const existingTargets: any[] = [];
        this.overviewData.forEach(d =>
            d.monthlyTargets.forEach((amount, i) => {
                existingTargets.push({
                    division: d.division,
                    year: this.currentYear,
                    month: i + 1,
                    targetAmount: amount,
                });
            })
        );

        modalRef.componentInstance.year = this.currentYear;
        modalRef.componentInstance.divisions = activeDivisions.length > 0 ? activeDivisions : this.divisionOrder;
        modalRef.componentInstance.divisionLabels = this.divisionLabels;
        modalRef.componentInstance.existingTargets = existingTargets;

        modalRef.result.then(
            (saved) => {
                if (saved) this.fetchData();
            },
            () => {} // dismissed
        );
    }

    openBusinessDeveloperTargetModal(): void {
        const modalRef = this.modalService.open(BusinessDeveloperTargetModalComponent, {
            windowClass: 'modal-prompt modal-prompt--wide',
            animation: false,
        });

        const existingTargets: any[] = [];
        this.businessDeveloperOverviewData.forEach(businessDeveloper =>
            businessDeveloper.monthlyTargets.forEach((amount, i) => {
                existingTargets.push({
                    businessDeveloperUuid: businessDeveloper.businessDeveloperUuid,
                    businessDeveloperName: businessDeveloper.businessDeveloperName,
                    year: this.currentYear,
                    month: i + 1,
                    targetAmount: amount,
                });
            })
        );

        modalRef.componentInstance.year = this.currentYear;
        modalRef.componentInstance.businessDevelopers = this.businessDeveloperOverviewData;
        modalRef.componentInstance.existingTargets = existingTargets;

        modalRef.result.then(
            (saved) => {
                if (saved) this.fetchData();
            },
            () => {}
        );
    }

    // ── Data Source Toggle ─────────────────────────────────────────────────────

    toggleDataSource(): void {
        const newSource = this.dataSource === 'RESUMO' ? 'YUKI' : 'RESUMO';
        this.subscriptions.add(
            this.financialService.updateTargetsDataSource(newSource).subscribe({
                next: () => {
                    this.dataSource = newSource;
                    this.toastr.success(`Data source switched to ${newSource}`);
                },
                error: () => {
                    this.toastr.error('Failed to update data source');
                },
            })
        );
    }

    // ── Data Fetching ──────────────────────────────────────────────────────────

    private fetchData(): void {
        this.loadingData = true;
        this.reconciliationResult = null;
        this.yukiCheckDone = false;
        const year = this.currentYear;

        this.subscriptions.add(
            forkJoin({
                divisionOverview: this.financialService.getDivisionTurnoverOverview(year),
                businessDeveloperOverview: this.financialService.getBusinessDeveloperTurnoverOverview(year),
            }).subscribe({
                next: ({ divisionOverview, businessDeveloperOverview }) => {
                    this.overviewData = divisionOverview;
                    this.businessDeveloperOverviewData = businessDeveloperOverview;
                    if (this.selectedBusinessDeveloperUuid !== DivisionGraphsComponent.ALL_BUSINESS_DEVELOPERS
                        && !businessDeveloperOverview.some(d => d.businessDeveloperUuid === this.selectedBusinessDeveloperUuid)) {
                        this.selectedBusinessDeveloperUuid = DivisionGraphsComponent.ALL_BUSINESS_DEVELOPERS;
                    }
                    this.loadingData = false;
                    this.runYukiCheck();
                },
                error: () => {
                    this.overviewData = [];
                    this.businessDeveloperOverviewData = [];
                    this.selectedBusinessDeveloperUuid = DivisionGraphsComponent.ALL_BUSINESS_DEVELOPERS;
                    this.loadingData = false;
                },
            })
        );
    }

    // ── Send Email ───────────────────────────────────────────────────────────

    sendingEmail = false;

    sendOverviewEmail(): void {
        const email = this.store.selectSnapshot(UserState.userEmail);
        if (!email) {
            this.toastr.error('No email address found for your account.');
            return;
        }
        this.sendingEmail = true;
        this.subscriptions.add(
            this.financialService.sendTargetsOverviewEmail(this.currentYear, email).subscribe({
                next: () => {
                    this.toastr.success(`Targets overview sent to ${email}`);
                    this.sendingEmail = false;
                },
                error: () => {
                    this.toastr.error('Failed to send email. Please try again.');
                    this.sendingEmail = false;
                },
            })
        );
    }

    // ── Yuki Reconciliation ──────────────────────────────────────────────────

    loadingReconciliation = false;
    yukiCheckDone = false;
    reconciliationResult: YukiReconciliationResult | null = null;
    showDiscrepancies = false;

    // ── Yuki Accounting Verification ─────────────────────────────────────────
    loadingYukiVerification = false;
    yukiVerificationDone = false;
    yukiVerification: YukiAccountingVerification | null = null;
    private yukiVerificationMap: { [division: string]: DivisionAccountingVerification } = {};

    get yukiCheckOk(): boolean {
        return this.reconciliationResult != null && this.reconciliationResult.missingInResumo.length === 0;
    }

    get yukiDiscrepancyCount(): number {
        return this.reconciliationResult ? this.reconciliationResult.missingInResumo.length : 0;
    }

    private runYukiCheck(): void {
        this.loadingReconciliation = true;
        this.yukiCheckDone = false;
        this.showDiscrepancies = false;
        this.subscriptions.add(
            this.financialService.reconcileWithYuki(this.currentYear).subscribe({
                next: (result) => {
                    this.reconciliationResult = result;
                    this.loadingReconciliation = false;
                    this.yukiCheckDone = true;
                },
                error: () => {
                    this.loadingReconciliation = false;
                    this.yukiCheckDone = true;
                },
            })
        );

        // Also run accounting verification
        this.loadingYukiVerification = true;
        this.yukiVerificationDone = false;
        this.yukiVerification = null;
        this.yukiVerificationMap = {};
        this.subscriptions.add(
            this.financialService.verifyWithYukiAccounting(this.currentYear).subscribe({
                next: (result) => {
                    this.yukiVerification = result;
                    this.loadingYukiVerification = false;
                    this.yukiVerificationDone = true;
                    if (result.divisions) {
                        result.divisions.forEach(d => this.yukiVerificationMap[d.division] = d);
                    }
                },
                error: () => {
                    this.loadingYukiVerification = false;
                    this.yukiVerificationDone = true;
                },
            })
        );
    }

    getDivisionYukiStatus(division: string): 'ok' | 'mismatch' | 'loading' | 'none' {
        if (this.loadingYukiVerification) return 'loading';
        if (!this.yukiVerificationDone) return 'none';
        const v = this.yukiVerificationMap[division];
        if (!v) return 'none';
        return v.yearMatch ? 'ok' : 'mismatch';
    }

    getDivisionYukiTooltip(division: string): string {
        const v = this.yukiVerificationMap[division];
        if (!v) return '';
        if (v.yearMatch) {
            return `Yuki (${v.glAccountCode}): ${this.formatCurrencyFull(v.yukiYearTotal)} ✓`;
        }
        return `Yuki (${v.glAccountCode}): ${this.formatCurrencyFull(v.yukiYearTotal)} — Resumo: ${this.formatCurrencyFull(v.resumoYearTotal)} (verschil: ${this.formatCurrencyFull(v.yukiYearTotal - v.resumoYearTotal)})`;
    }

    getMonthYukiStatus(division: string, monthIndex: number): 'ok' | 'mismatch' | 'none' {
        const v = this.yukiVerificationMap[division];
        if (!v || !v.monthMatch) return 'none';
        // Only show status for months that have data in either system
        if (v.resumoMonthly[monthIndex] === 0 && v.yukiMonthly[monthIndex] === 0) return 'none';
        return v.monthMatch[monthIndex] ? 'ok' : 'mismatch';
    }

    getMonthYukiTooltip(division: string, monthIndex: number): string {
        const v = this.yukiVerificationMap[division];
        if (!v) return '';
        const resumo = v.resumoMonthly[monthIndex] || 0;
        const yuki = v.yukiMonthly[monthIndex] || 0;
        if (resumo === 0 && yuki === 0) return '';
        if (v.monthMatch[monthIndex]) {
            return `Yuki: ${this.formatCurrencyFull(yuki)} ✓`;
        }
        return `Yuki: ${this.formatCurrencyFull(yuki)} — Resumo: ${this.formatCurrencyFull(resumo)}`;
    }

    getTotalMonthYukiStatus(monthIndex: number): 'ok' | 'mismatch' | 'none' {
        if (!this.yukiVerificationDone || !this.yukiVerification?.divisions) return 'none';
        let totalResumo = 0, totalYuki = 0;
        for (const v of this.yukiVerification.divisions) {
            totalResumo += v.resumoMonthly[monthIndex] || 0;
            totalYuki += v.yukiMonthly[monthIndex] || 0;
        }
        if (totalResumo === 0 && totalYuki === 0) return 'none';
        return Math.abs(totalResumo - totalYuki) <= 0.50 ? 'ok' : 'mismatch';
    }

    getTotalMonthYukiTooltip(monthIndex: number): string {
        if (!this.yukiVerification?.divisions) return '';
        let totalResumo = 0, totalYuki = 0;
        for (const v of this.yukiVerification.divisions) {
            totalResumo += v.resumoMonthly[monthIndex] || 0;
            totalYuki += v.yukiMonthly[monthIndex] || 0;
        }
        if (totalResumo === 0 && totalYuki === 0) return '';
        if (Math.abs(totalResumo - totalYuki) <= 0.50) {
            return `Yuki totaal: ${this.formatCurrencyFull(totalYuki)} ✓`;
        }
        return `Yuki totaal: ${this.formatCurrencyFull(totalYuki)} — Resumo: ${this.formatCurrencyFull(totalResumo)}`;
    }

    getTotalYearYukiStatus(): 'ok' | 'mismatch' | 'none' {
        if (!this.yukiVerificationDone || !this.yukiVerification?.divisions) return 'none';
        let totalResumo = 0, totalYuki = 0;
        for (const v of this.yukiVerification.divisions) {
            totalResumo += v.resumoYearTotal || 0;
            totalYuki += v.yukiYearTotal || 0;
        }
        if (totalResumo === 0 && totalYuki === 0) return 'none';
        return Math.abs(totalResumo - totalYuki) <= 0.50 ? 'ok' : 'mismatch';
    }

    getTotalYearYukiTooltip(): string {
        if (!this.yukiVerification?.divisions) return '';
        let totalResumo = 0, totalYuki = 0;
        for (const v of this.yukiVerification.divisions) {
            totalResumo += v.resumoYearTotal || 0;
            totalYuki += v.yukiYearTotal || 0;
        }
        if (Math.abs(totalResumo - totalYuki) <= 0.50) {
            return `Yuki totaal: ${this.formatCurrencyFull(totalYuki)} ✓`;
        }
        return `Yuki totaal: ${this.formatCurrencyFull(totalYuki)} — Resumo: ${this.formatCurrencyFull(totalResumo)}`;
    }

    toggleDiscrepancies(): void {
        this.showDiscrepancies = !this.showDiscrepancies;
    }

    goToInvoice(yukiId: string): void {
        // Navigate to sales invoices folder (folderId=2) with the document highlighted
        this.router.navigate(['/invoices/overview'], { queryParams: { folder: 2, doc: yukiId } });
    }

    // ── Helpers ────────────────────────────────────────────────────────────────

    formatCurrency(value: number): string {
        if (value === 0) return '-';
        return new Intl.NumberFormat('nl-NL', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(value);
    }

    formatCurrencyFull(value: number): string {
        return new Intl.NumberFormat('nl-NL', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(value);
    }

    getTargetPercentage(turnover: number, target: number): number {
        if (target <= 0) return 0;
        return Math.round((turnover / target) * 100);
    }

    trackByDivision(index: number, item: DivisionTurnoverOverview): string {
        return item.division;
    }
}
